import { useState } from 'react'
import { AvatarWithFallback } from '@/components'
import { DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components'
import AlertTriangle from '@/assets/icons/alert-triangle'
import { Divider, Button } from '@/components'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import PropTypes from 'prop-types'
import { Icons } from '@/theme/Images'
import { ToastMessage } from '@/components/toast-message'
import { toast } from 'sonner'
import { format } from 'date-fns'
import Amount from '@/pages/transactions-page/Amount'
import { transformAmount } from '@/utils/string-format-utils'
import { useAuth } from '@/contexts/auth.context'
import LoadingScreen from '@/components/loading-screen'
import { convertUTCToET } from '@/utils/date-format-util'

// Define constants for each option
const GENERAL_QUESTION = 'General Question'
const REWARDS = 'Rewards'
const I_NEED_TO_DISPUTE = 'I Need to Dispute'

function ReportProblemDialog({ closeDialog, transaction }) {
  const [selectedReason, setSelectedReason] = useState(GENERAL_QUESTION)

  const { isLoading: isCustomerDataLoading, user } = useAuth()

  async function submitReplaceCard() {
    const zendeskFastenUrl = import.meta.env.VITE_ZENDESK_FASTEN_URL
    const zendeskHighnoteUrl = import.meta.env.VITE_ZENDESK_HIGHNOTE_URL

    //params for URL construction
    const email = user?.customer?.email
    const financialAccountId = user?.customer?.financialAccountId
    const transactionDate = transaction?.transactionDate
    const transactionAmount = transformAmount(transaction?.amount?.value, transaction?.sign).replace(/\s?\$/g, '')
    const merchantName = (transaction?.merchantName || transaction?.merchantDetails?.name)?.replace(/\s+/g, '-')
    const transactionId = transaction?.id

    // Construct the Fasten Zendesk URL with query parameters
    const fastenUrlWithParams = `${zendeskFastenUrl}&tf_33278072912403=${encodeURIComponent(merchantName)}&tf_33278111662995=${transactionAmount}&tf_33278065317011=${transactionDate}&tf_32234787345165=${transactionId}&tf_33278033734931=${financialAccountId}&tf_anonymous_requester_email=${email}`

    // Construct the Zendesk URL with query parameters
    const highnoteUrlWithParams = `${zendeskHighnoteUrl}&tf_subject=Your+Dispute+Claim+Has+Been+Submitted&tf_18448459539597=${encodeURIComponent(merchantName)}&tf_18448952317965=${transactionAmount}&tf_18448501866637=${transactionDate}&tf_32234787345165=${transactionId}&tf_18448341785485=${financialAccountId}&tf_anonymous_requester_email=${email}`

    // Determine final URL based on selected dispute reason
    const targetUrl =
      selectedReason === GENERAL_QUESTION || selectedReason === REWARDS ? fastenUrlWithParams : highnoteUrlWithParams

    // Redirect to Zendesk URL
    window.open(targetUrl, '_blank')

    closeDialog()
    setSelectedReason(GENERAL_QUESTION)
    toast.custom(() => (
      <ToastMessage
        success
        text="Problem reported successfully"
        description="Your problem has been reported successfully. Our team will review the details and follow up with you shortly"
      />
    ))
  }

  function handleDialogOpenClose() {
    closeDialog()
    setSelectedReason(GENERAL_QUESTION)
  }

  closeDialog = () => {
    console.log('closed')
  }

  return (
    <DialogContent className="md:w-[490px] md:h-[81vh] h-[78vh] w-[92vw]  rounded-lg" onClose={handleDialogOpenClose}>
      <div className="flex flex-col">
        <div className="flex flex-col gap-1 md:w-[368px] w-full"></div>
        <DialogHeader className="md:mb-[-20px] mt-[-3px]">
          <div className="flex flex-row items-center gap-4 mb-4">
            <div className="flex items-center justify-center bg-gradient-to-r from-[#011824] to-[#00A6B6] w-12 h-12 rounded-[10px]">
              <AlertTriangle />
            </div>
            <div className="flex flex-col gap-1 md:w-[368px] w-[230px] h-[40px]">
              <DialogTitle className="text-[#101828] mb-1">Report a Problem</DialogTitle>
              <DialogDescription className="text-[#475467]">{`What doesn't look right with this transaction?`}</DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <Divider />
      </div>

      <div className="flex flex-col gap-5 pl-7 pt-1 overflow-y-auto overflow-x-hidden ">
        <div className="flex flex-col gap-2">
          <p className="text-base font-normal text-[--gray-600] md:w-[431px] md:h-[48px] w-[269px] h-[48px] mt-4">
            Choose the scenario that best describes what happened.
          </p>
          <p className="text-base font-normal text-[--gray-600]"></p>
        </div>
        <div className="flex flex-col gap-[14px] pb-3 md:w-[432px]">
          <p className="text-md font-medium text-[--gray-700] text-[14px]">Transaction</p>

          <div className="flex flex-row items-center text-sm md:p-4 pl-2 rounded-[12px]  md:w-[432px] md:h-[104px] w-[289px] h-[76px] border-2 border-solid border-brand-600">
            <div className="flex items-center pt-4 pb-4 pl-0 border-gray-200">
              <AvatarWithFallback source="https://github.com/shadcn.png" fallback="NI" className="mr-2" />
              <div className="ml-1 flex flex-col justify-between min-h-[40px]">
                <p className="text-sm font-medium text-[--gray-900]">
                  {transaction?.merchantName || transaction?.merchantDetails?.name || ''}
                </p>
                <p className="text-sm font-regular text-[#475467]">{transaction?.merchantDetails?.description || ''}</p>
              </div>
            </div>
            {/* Add margin to create a gap */}
            <div className="flex items-center pt-4 pr-6 pb-4 pl-6 justify-end border-gray-200 ml-auto">
              <div>
                <p className="ml-1 text-sm font-bold  flex justify-end">
                  <Amount
                    amount={transaction?.amount?.value}
                    sign={transaction?.sign}
                    customColor="text-[--gray-900]"
                  />
                </p>
                <p className="ml-8 text-sm font-regular text-[#475467] flex justify-end">
                  {transaction?.transactionDate
                    ? format(new Date(convertUTCToET(transaction.transactionDate)), 'MM/dd/yy')
                    : 'Date'}
                </p>
              </div>
            </div>
          </div>
        </div>
        {isCustomerDataLoading ? (
          <LoadingScreen />
        ) : (
          <>
            {/* Define Radio Buttons */}
            <RadioGroup
              defaultValue={GENERAL_QUESTION}
              value={selectedReason}
              onValueChange={value => {
                setSelectedReason(value)
              }}
              className="md:w-[432px] w-[279px] h-[800px] md:ml-4 ml-2 "
            >
              {/*Define contents*/}
              {/* Option one */}
              <div className="md:ml-2 ml-3">
                <div
                  className="flex items-center space-x-2  md:w-[320px] md:h-[20px] w-[239px] h-[20px] cursor-pointer"
                  onClick={() => setSelectedReason(GENERAL_QUESTION)}
                >
                  {selectedReason === GENERAL_QUESTION ? (
                    <RadioGroupItem value={GENERAL_QUESTION} id={GENERAL_QUESTION} selection={selectedReason} />
                  ) : (
                    <img src={Icons.checkboxUncheckedCircleIcon} className="w-4" alt="Unchecked Icon" />
                  )}
                  <Label
                    className={`text-[--gray-700] font-medium cursor-pointer ${
                      selectedReason === GENERAL_QUESTION ? 'font-bold text-[--gray-600]' : ''
                    }`}
                    htmlFor={GENERAL_QUESTION}
                  >
                    General question
                  </Label>
                </div>
                <div
                  className="ml-6 text-sm md:w-[320px] md:h-[40px] w-[239px] h-[40px] mb-3 text-[--gray-600] font-normal cursor-pointer"
                  onClick={() => setSelectedReason(GENERAL_QUESTION)}
                >
                  I have a general question I’d like to ask customer service.
                </div>
              </div>

              {/* Option two */}
              <div className="md:ml-2 ml-3">
                <div
                  className="flex items-center space-x-2  md:w-[320px] md:h-[20px] w-[239px] h-[20px] cursor-pointer"
                  onClick={() => setSelectedReason(REWARDS)}
                >
                  {selectedReason === REWARDS ? (
                    <RadioGroupItem value={REWARDS} id={REWARDS} selection={selectedReason} />
                  ) : (
                    <img src={Icons.checkboxUncheckedCircleIcon} className="w-4" alt="Unchecked Icon" />
                  )}
                  <Label
                    className={`text-[--gray-700] font-medium cursor-pointer ${
                      selectedReason === REWARDS ? 'font-bold text-[--gray-600]' : ''
                    }`}
                    htmlFor={REWARDS}
                  >
                    Rewards
                  </Label>
                </div>
                <div
                  className="ml-6 text-sm md:w-[320px] md:h-[40px] w-[239px] h-[60px] mb-3 text-[--gray-600] font-normal cursor-pointer"
                  onClick={() => setSelectedReason(REWARDS)}
                >
                  There is an issue with the rewards category assigned or point allocation.
                </div>
              </div>

              {/* Option Three */}
              <div className="md:ml-2 ml-3">
                <div
                  className="flex items-center space-x-2  md:w-[320px] md:h-[20px] w-[239px] h-[20px] cursor-pointer"
                  onClick={() => setSelectedReason(I_NEED_TO_DISPUTE)}
                >
                  {selectedReason === I_NEED_TO_DISPUTE ? (
                    <RadioGroupItem value={I_NEED_TO_DISPUTE} id={I_NEED_TO_DISPUTE} selection={selectedReason} />
                  ) : (
                    <img src={Icons.checkboxUncheckedCircleIcon} className="w-4" alt="Unchecked Icon" />
                  )}
                  <Label
                    className={`text-[--gray-700] font-medium cursor-pointer ${
                      selectedReason === I_NEED_TO_DISPUTE ? 'font-bold text-[--gray-600]' : ''
                    }`}
                    htmlFor={I_NEED_TO_DISPUTE}
                  >
                    I need to dispute
                  </Label>
                </div>
                <div
                  className="ml-6 text-sm md:w-[320px] md:h-[40px] w-[239px] h-[60px] mb-12 text-[--gray-600] font-normal cursor-pointer"
                  onClick={() => setSelectedReason(I_NEED_TO_DISPUTE)}
                >
                  Dispute a charge if you didn’t authorize it, were billed incorrectly, charged for a canceled or
                  returned item, didn’t receive what you paid for, or experienced issues with a product or service.
                </div>
              </div>
            </RadioGroup>
          </>
        )}
        <div className="ml-[-25px] mt-[25px] md:mt-[10px]">
          <Divider />
        </div>
        {/* Buttons */}
        <div>
          <DialogFooter className="flex flex-row justify-evenly p-0 mb-6 md:gap-3 gap-0">
            <DialogClose asChild>
              <Button
                id="report-problem-cancel-button"
                aria-label="Cancel"
                variant="outline"
                className="md:w-[210px] md:h-[44px] w-[133.5px] h-[44px] hover:bg-transparent md:text-base text-sm mt-[6px] md:ml-[-5px] ml-[-12px]"
              >
                Cancel
              </Button>
            </DialogClose>
            <DialogClose asChild>
              <Button
                id="report-problem-button"
                aria-label="Report Problem"
                type="submit"
                className="md:w-[210px] md:h-[44px] w-[133.5px] h-[44px] md:text-base text-sm bg-[#B42318] hover:bg-[#B42318] focus:bg-[#B42318] mt-[6px] md:mr-6 mr-4"
                onClick={submitReplaceCard}
              >
                Report problem
              </Button>
            </DialogClose>
          </DialogFooter>
        </div>
      </div>
    </DialogContent>
  )
}

ReportProblemDialog.propTypes = {
  closeProblem: PropTypes.func.isRequired,
  closeDialog: PropTypes.func.isRequired,
  transaction: PropTypes.object.isRequired,
}

export default ReportProblemDialog
